import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, Crown, Star, Info } from "lucide-react";
import "swiper/css";

import { TokenContext } from "../components/TokenContext";
import { callProxy } from "../api/api";
import { getApartmentImage } from "../utils/imageHelper";
import Sidebar from "../components/Sidebar"; // ✅ Imported Sidebar
// import "./sidebar.css";
import "../components/sidebar.css";
import MobileFilters from "../components/MobileFilters";
import { format } from "date-fns";
import { normalizeAvailability } from "../utils/normalizeAvailability";
import SearchLoader from "../components/SearchLoader";


export default function Results() {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const arrival = searchParams.get("arrival");
  const departure = searchParams.get("departure");
  // const isMobile = window.innerWidth <= 768;
const isMobile = useIsMobile(900);
const [searchLoading, setSearchLoading] = useState(true);


  const { token } = useContext(TokenContext);
  const navigate = useNavigate();
  const [loadingApartmentId, setLoadingApartmentId] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return format(new Date(dateString), "MMM dd, yyyy");
  };


  // const [availability, setAvailability] = useState(state?.availability || null);
  const [availability, setAvailability] = useState(
  state?.availability ? normalizeAvailability(state.availability) : null
);

  const isAvailabilityReady = availability !== null;

  
// useEffect(() => {
//   const fetchAvailability = async () => {
//     if (!arrival || !departure) return;

//     setSearchLoading(true); // ✅ START LOADER

//     try {
//       const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";

//       const res = await fetch(`${API_BASE}/api/check-availability`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           arrivalDate: arrival,
//           departureDate: departure,
//         }),
//       });

//       const raw = await res.json();
//       setAvailability(normalizeAvailability(raw));
//     } catch (err) {
//       console.error("Failed to reload availability", err);
//     } finally {
//       setSearchLoading(false); // ✅ STOP LOADER
//     }
//   };

//   fetchAvailability();
// }, [arrival, departure]);


useEffect(() => {
    const fetchAvailability = async () => {
      if (!arrival || !departure) return;

      setSearchLoading(true);

      try {
        const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";

        // 🔴 WAS: fetch(`${API_BASE}/api/check-availability`...
        // ✅ FIX: Added "/rates" because route is mounted there
        const res = await fetch(`${API_BASE}/api/rates/check-availability`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            arrivalDate: arrival,
            departureDate: departure,
          }),
        });

        if (!res.ok) throw new Error("Availability check failed");

        const raw = await res.json();
        setAvailability(normalizeAvailability(raw));
      } catch (err) {
        console.error("Failed to reload availability", err);
      } finally {
        setSearchLoading(false);
      }
    };

    fetchAvailability();
  }, [arrival, departure]);

const destination = searchParams.get("dest") || "Available stays";

const guests = {
  adults: Number(searchParams.get("adults") || 0),
  children: Number(searchParams.get("children") || 0),
  rooms: Number(searchParams.get("rooms") || 1),
  pets: searchParams.get("pets") === "true",
};

  // --- Data State ---
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- Unified Filter State ---
  const [filters, setFilters] = useState({
    maxBudget: 2000000,
    rating: "any",
    types: [],
    amenities: []
  });

  // --- Handler for Sidebar ---
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  /* -----------------------------
      FETCH APARTMENTS
  ----------------------------- */



const fetchApartments = useCallback(async () => {
  setLoading(true);
  try {
    const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";

    const res = await fetch(`${API_BASE}/api/apartments`);
    if (!res.ok) throw new Error("Failed to load properties");

    const list = await res.json();

    setApartments(list);
  } catch (e) {
    console.error("❌ Failed loading properties:", e);
  }
  setLoading(false);
}, []);


//Loader useffect
useEffect(() => {
  if (
    availability &&
    apartments.length > 0
  ) {
    setSearchLoading(false);
  }
}, [availability, apartments]);
//Loader useffect end

  useEffect(() => {
    fetchApartments();
  }, [fetchApartments]);

  /* -----------------------------
      NIGHT COUNT
  ----------------------------- */


  const nights = useMemo(() => {
    if (!arrival || !departure) return 1;
    return Math.max(
      1,
      Math.ceil(
        (new Date(departure) - new Date(arrival)) /
        (1000 * 60 * 60 * 24)
      )
    );
  }, [arrival, departure]);

  /* -----------------------------
      APARTMENT MAP
  ----------------------------- */

const apartmentMap = useMemo(() => {
  const map = {};
  apartments.forEach((a) => {
    const id = a.smoobuId ?? a.id; // ✅ SUPPORT BOTH
    if (id) {
      map[String(id)] = a;
    }
  });
  return map;
}, [apartments]);



  /* -----------------------------
      FILTERING LOGIC
  ----------------------------- */

// const availableIds = useMemo(() => {
//   if (!availability) return [];

//   if (Array.isArray(availability.availableApartments))
//     return availability.availableApartments;

//   if (Array.isArray(availability.available))
//     return availability.available;

//   if (Array.isArray(availability?.data?.availableApartments))
//     return availability.data.availableApartments;

//   console.warn("No available apartments found in availability:", availability);
//   return [];
// }, [availability]);
const availableIds = useMemo(() => {
  return availability?.availableApartments || [];
}, [availability]);



const filteredIds = useMemo(() => {
  if (!availableIds.length) return [];
  if (!apartments.length) return []; 

  let ids = [...availableIds];

  if (filters.rating !== "any") {
    const minRating = parseInt(filters.rating, 10);
    ids = ids.filter((id) => (apartmentMap[id]?.rating || 0) >= minRating);
  }

  if (filters.types.length > 0) {
    ids = ids.filter((id) => {
      const aptType = apartmentMap[id]?.type?.toLowerCase() || "apartment";
      return filters.types.includes(aptType);
    });
  }

  if (filters.amenities.length > 0) {
    ids = ids.filter((id) => {
      const ams = apartmentMap[id]?.amenities || [];
      return filters.amenities.every((req) =>
        ams.some((a) => a.toLowerCase().includes(req.toLowerCase()))
      );
    });
  }

  return ids.sort((a, b) => {
    const A = apartmentMap[a]?.isPremium || apartmentMap[a]?.rating >= 9 ? 1 : 0;
    const B = apartmentMap[b]?.isPremium || apartmentMap[b]?.rating >= 9 ? 1 : 0;
    return B - A;
  });
}, [availableIds, apartmentMap, filters]);


function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    window.matchMedia(`(max-width: ${breakpoint}px)`).matches
  );

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = () => setIsMobile(media.matches);
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, [breakpoint]);

  return isMobile;
}

//Card Click navigate
const handleCardClick = (apartment) => {
  navigate(`/apartments/${apartment.slug}`, {
    state: {
      apartment,
      arrival,
      departure,
      guests,
    },
  });
};



  /* -----------------------------
      RENDER
  ----------------------------- */
//Stripe Payment
const handleDirectStripeCheckout = async ({
  apartment,
  totalPrice,
}) => {
  try {
    setLoadingApartmentId(apartment.smoobuId);

    const meta = {
      apartmentId: String(apartment.smoobuId),
      apartmentName: apartment.name?.en || "Unnamed",
      arrivalDate: arrival,
      departureDate: departure,
      adults: String(guests.adults),
      children: String(guests.children),
      price: String(totalPrice),
      bookingSource: "Website-Results",
    };

    const res = await callProxy("/api/create-checkout-session", token, {
      method: "POST",
      body: {
        apartmentId: meta.apartmentId,
        price: totalPrice,
        meta,
      },
    });

    if (!res?.data?.url) {
      throw new Error("Stripe session not created");
    }

    window.location.href = res.data.url;
  } catch (err) {
    console.error("Stripe redirect failed:", err);
    alert("Payment could not be initiated. Please try again.");
    setLoadingApartmentId(null);
  }
};


useEffect(() => {
  console.log("AVAILABILITY:", availability);
  console.log("AVAILABLE IDS:", availableIds);
  console.log("APARTMENT MAP KEYS:", Object.keys(apartmentMap));
}, [availability, availableIds, apartmentMap]);


return (
  <div className="page-container">

    {/* HEADER */}
    <header className="results-header">
      <div className="header-inner">
        <div>
          <h1>{destination || "Available stays"}</h1>
          <p className="subtitle">
            {formatDate(arrival)} — {formatDate(departure)} • {" "}  {guests.adults} adults, {guests.children} children
          </p>
        </div>
        <div className="header-badge">{nights} Nights</div>
      </div>
    </header>

    {/* ✅ MOBILE FILTER BAR — FLEX ROW */}
    {isMobile && (
      <div className="max-w-[1200px] mx-auto px-4">
        <MobileFilters
          filters={filters}
          onFilterChange={handleFilterChange}
        />
      </div>
    )}

    {searchLoading && <SearchLoader />}


    {/* MAIN LAYOUT */}
    <div className="main-layout">

      {/* DESKTOP SIDEBAR ONLY */}
      {!isMobile && (
        <div className="sidebar-wrapper">
          <Sidebar
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>
      )}

      {/* RESULTS */}
      <main className="results-area">
       {!isAvailabilityReady && (
          <div className="no-data">Checking availability…</div>
        )}
        {loading && <div className="loading-state">Finding best rates...</div>}

        {!loading && apartments.length === 0 && (
          <div className="no-data">Loading properties…</div>
        )}

        {!loading && apartments.length > 0 && filteredIds.length === 0 && (
          <div className="empty-state">
            <h3>No properties match your filters</h3>
            <button
              onClick={() =>
                setFilters({
                  maxBudget: 20000,
                  rating: "any",
                  types: [],
                  amenities: [],
                })
              }
            >
              Reset Filters
            </button>
          </div>
        )}


          <div className="cards-grid">
            {filteredIds.map((id) => {
              const apartment = apartmentMap[id];
              if (!apartment) return null;

             
              // --- Price Logic ---
              const apiPrice = availability.prices?.[id];
              const suggested = availability.suggestedDates?.[id];

              const effectiveNights = suggested
                ? Math.ceil(
                    (new Date(suggested.to) - new Date(suggested.from)) / 86400000
                  )
                : nights;

              const baseNightlyDefault =
                apartment.price_per_night || apartment.amount || 0;

              // ✅ Base price (before VAT)
              const baseTotal =
                apiPrice?.price || baseNightlyDefault * effectiveNights;

              // ✅ VAT
              const VAT_RATE = 0.05;
              const totalWithVat = Math.round(baseTotal * (1 + VAT_RATE));

              // 🛑 Budget filter MUST use VAT-inclusive price
              if (totalWithVat > filters.maxBudget) return null;

              // ✅ Premium discount
              const isPremium = apartment.isPremium || apartment.rating >= 9;
              const discount = isPremium ? 0.1 : 0;

              // ✅ FINAL price (VAT + discount)
              const finalPriceWithTax = discount
                ? Math.round(totalWithVat * (1 - discount))
                : totalWithVat;

              // ✅ Per-night (VAT included)
              const baseNightly = Math.round(finalPriceWithTax / effectiveNights);


              // --- Card Render ---
              return (
                
                <div
                  className={`property-card ${isPremium ? "premium-glow" : ""}`}
                  onClick={!searchLoading ? () => handleCardClick(apartment) : undefined}
                  style={{ cursor: searchLoading ? "default" : "pointer" }}
                >
                  {/* <div key={id} className={`property-card ${isPremium ? "premium-glow" : ""}`}> */}
                  
                  {/* Image */}
                  <div className="card-image-box">
                    <img
                  src={getApartmentImage(apartment)}
                  alt={apartment.name}
                  onError={(e) => {
                    e.currentTarget.src = "/assets/images/placeholder.jpg";
                  }}
                />


                    {isPremium && (
                      <span className="badge-premium">
                        <Crown size={12} fill="currentColor" /> Premium
                      </span>
                    )}
                    <span className="badge-rating">
                      <Star size={12} fill="#f59e0b" stroke="#f59e0b" />
                      {apartment.rating || "4.5"}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="card-content">
                    <div className="card-top">
                      <div className="card-header-row">
                        <h3>{apartment.name?.en || "Unnamed"}</h3>
                      </div>
                      <div className="location-row">
                        <MapPin size={14} />
                        {apartment.city?.en || destination}
                      </div>

                      <div className="amenities-row">
                       {apartment.amenities?.slice(0, 3).map((am, i) => (
                        <span key={i} className="pill">{am?.en || "N/A"}</span>
                      ))}
                      </div>

                      {suggested && (
                        <div className="date-warning">
                          <Info size={12} />
                          Dates changed: {suggested.from} - {suggested.to}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="card-footer">
                        <div className="price-stack">
                          {discount > 0 && (
                            <span className="price-old">
                              AED {totalWithVat.toLocaleString()}
                            </span>
                          )}

                          {/* PRICE + TAX LABEL */}
                          <div className="flex items-center gap-2">
                            <span className="price-main">
                              AED {finalPriceWithTax.toLocaleString()}
                            </span>
                            <span className="text-[11px] text-slate-500 whitespace-nowrap">
                              (5% tax included)
                            </span>
                          </div>

                          <span className="price-sub">
                            {effectiveNights} nights • AED {baseNightly.toLocaleString()}/night
                          </span>
                        </div>
                        {/* <button
                          className="btn-book"
                          disabled={loadingApartmentId === apartment.smoobuId}
                          onClick={() =>
                            handleDirectStripeCheckout({
                              apartment,
                              totalPrice: finalPriceWithTax,
                            })
                          }
                        >
                          {loadingApartmentId === apartment.smoobuId
                            ? "Redirecting…"
                            : "Book Now"}
                        </button> */}

                        <button
                          className="btn-book"
                          disabled={loadingApartmentId === apartment.smoobuId}
                          onClick={(e) => {
                            e.stopPropagation(); // ✅ PREVENT CARD CLICK
                            handleDirectStripeCheckout({
                              apartment,
                              totalPrice: finalPriceWithTax,
                            });
                          }}
                        >
                          {loadingApartmentId === apartment.smoobuId
                            ? "Redirecting…"
                            : "Book Now"}
                        </button>



                    </div>
                  </div>
                </div>
              );
            })}
          </div>
      </main>
    </div>
  </div>
);

}